/**
 * <smootify-markets-switcher> custom element.
 *
 * Convention HTML :
 *
 *   <smootify-markets-switcher>
 *     <!-- Toggle visible (label du market courant) -->
 *     <div market="current">
 *       <span market="current-flag"></span>
 *       <span market="current-name">France</span>
 *       <span market="current-currency">EUR</span>
 *     </div>
 *
 *     <!-- Liste : un template (1er enfant `[market="item"]`) cloné par market -->
 *     <ul class="markets-list">
 *       <li market="item">
 *         <a href="#" market="link">
 *           <span market="flag"></span>
 *           <span market="name">Country Name</span>
 *           <span market="currency">EUR</span>
 *         </a>
 *       </li>
 *     </ul>
 *   </smootify-markets-switcher>
 *
 * Comportement :
 *   1. Au boot : fetch la liste des markets depuis Storefront `localization` query
 *   2. Hydrate `[market="current-*"]` avec le market actif
 *   3. Clone le template `[market="item"]` une fois par market et hydrate
 *      les `[market="flag|name|currency"]`
 *   4. Click sur un item → setLocale(country, language) → invalidation cache +
 *      cart buyerIdentity update + reload page
 *
 * Optionnel : poser `data-skip-reload="true"` sur le wrapper pour ne PAS
 * reloader la page (pour SPA / app où l'intégrateur gère le re-render).
 */

import { fetchMarkets, type MarketEntry } from "../i18n/markets.js";
import { getCurrentCountry, getCurrentLocale, setLocale } from "../i18n/locale.js";

function flagEmoji(countryCode: string): string {
  // Convertit "FR" en émoji drapeau 🇫🇷 (Regional Indicator Symbol).
  if (!countryCode || countryCode.length !== 2) return "";
  const A = 0x1f1e6 - "A".charCodeAt(0);
  const code = countryCode.toUpperCase();
  return String.fromCodePoint(A + code.charCodeAt(0)) +
    String.fromCodePoint(A + code.charCodeAt(1));
}

function findCurrentMarket(
  markets: MarketEntry[],
  currentCountry: string,
): MarketEntry | undefined {
  return markets.find((m) => m.countryCode === currentCountry);
}

function hydrateCurrent(
  wrapper: HTMLElement,
  market: MarketEntry | undefined,
  currentLanguage: string,
): void {
  const flag = market ? flagEmoji(market.countryCode) : "";
  const name = market?.countryName ?? currentLanguage;
  const currency = market?.currencyCode ?? "";
  const symbol = market?.currencySymbol ?? "";

  wrapper.querySelectorAll<HTMLElement>('[market="current-flag"]').forEach((el) => {
    el.textContent = flag;
  });
  wrapper.querySelectorAll<HTMLElement>('[market="current-name"]').forEach((el) => {
    el.textContent = name;
  });
  wrapper.querySelectorAll<HTMLElement>('[market="current-currency"]').forEach((el) => {
    el.textContent = currency;
  });
  wrapper.querySelectorAll<HTMLElement>('[market="current-symbol"]').forEach((el) => {
    el.textContent = symbol;
  });
  wrapper.querySelectorAll<HTMLElement>('[market="current-language"]').forEach((el) => {
    el.textContent = currentLanguage;
  });
}

function fillItemClone(
  clone: HTMLElement,
  market: MarketEntry,
  isActive: boolean,
): void {
  clone.querySelectorAll<HTMLElement>('[market="flag"]').forEach((el) => {
    el.textContent = flagEmoji(market.countryCode);
  });
  clone.querySelectorAll<HTMLElement>('[market="name"]').forEach((el) => {
    el.textContent = market.countryName;
  });
  clone.querySelectorAll<HTMLElement>('[market="currency"]').forEach((el) => {
    el.textContent = market.currencyCode;
  });
  clone.querySelectorAll<HTMLElement>('[market="symbol"]').forEach((el) => {
    el.textContent = market.currencySymbol;
  });
  clone.querySelectorAll<HTMLElement>('[market="country-code"]').forEach((el) => {
    el.textContent = market.countryCode;
  });
  // Mark active si market courant
  clone.classList.toggle("lmdr-market-active", isActive);
  clone.setAttribute("data-country-code", market.countryCode);
  clone.setAttribute("data-language-code", market.languages[0]?.isoCode ?? "");
}

export class SmootifyMarketsSwitcherElement extends HTMLElement {
  private _hydrated = false;

  connectedCallback(): void {
    if (this._hydrated) return;
    this._hydrated = true;
    // Defere fetchMarkets (LOCALIZATION query, ~450ms) hors du critical path.
    // Sans ce defer, ca bloque en parallele du fetch /page-1-products.json
    // au boot et ralentit le 1er paint des cards. Avec defer :
    //   - Si user clique/focus le switcher avant le timeout, hydrate immediatement
    //   - Sinon, fire en background apres 1500ms (apres render initial)
    // Resultat : LOCALIZATION sort du critical path = -300 a -500ms gain.
    let triggered = false;
    const fire = (): void => {
      if (triggered) return;
      triggered = true;
      void this.hydrate();
    };
    // Trigger 1 : interaction user (priorite)
    this.addEventListener("pointerenter", fire, { once: true, passive: true });
    this.addEventListener("focusin", fire, { once: true, passive: true });
    this.addEventListener("click", fire, { once: true, passive: true, capture: true });
    // Trigger 2 : background timeout, dans 1500ms (apres LCP probable)
    if (typeof window !== "undefined") {
      const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void }).requestIdleCallback;
      if (typeof ric === "function") {
        ric(fire, { timeout: 2000 });
      } else {
        window.setTimeout(fire, 1500);
      }
    }
  }

  private async hydrate(): Promise<void> {
    let state;
    try {
      state = await fetchMarkets();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[lmdr] markets-switcher: failed to fetch markets", err);
      return;
    }

    const currentCountry = getCurrentCountry();
    const currentLanguage = getCurrentLocale();
    const currentMarket = findCurrentMarket(state.countries, currentCountry);

    hydrateCurrent(this, currentMarket, currentLanguage);

    // Repère le template item (1er `[market="item"]`)
    const template = this.querySelector<HTMLElement>('[market="item"]');
    if (!template) {
      // Pas de template → on a au moins hydraté le current. OK.
      return;
    }
    const container = template.parentElement;
    if (!container) return;

    // Cache + retire les anciens clones (ré-hydratation safe)
    container
      .querySelectorAll('[data-lmdr-market-clone="true"]')
      .forEach((n) => n.remove());
    template.style.display = "none";
    template.setAttribute("data-lmdr-multi-template", "true");

    state.countries.forEach((m) => {
      const clone = template.cloneNode(true) as HTMLElement;
      clone.removeAttribute("data-lmdr-multi-template");
      clone.setAttribute("data-lmdr-market-clone", "true");
      clone.style.removeProperty("display");
      fillItemClone(clone, m, m.countryCode === currentCountry);
      container.appendChild(clone);
    });

    // Bind clicks (event delegation au wrapper pour idempotence)
    if (!this.hasAttribute("data-lmdr-bound")) {
      this.setAttribute("data-lmdr-bound", "true");
      this.addEventListener("click", (ev) => {
        const target = (ev.target as HTMLElement | null)?.closest<HTMLElement>(
          '[data-lmdr-market-clone="true"]',
        );
        if (!target) return;
        ev.preventDefault();
        ev.stopPropagation();
        const cc = target.getAttribute("data-country-code");
        const lc = target.getAttribute("data-language-code") || undefined;
        if (!cc) return;
        const skipReload = this.getAttribute("data-skip-reload") === "true";
        void setLocale({ country: cc, language: lc }, { reload: !skipReload });
      });

      // En skip-reload mode, re-toggle active classes + re-hydrate current
      // au locale_changed event (sinon l'UI reste figée sur l'ancien actif).
      document.addEventListener("smootify:locale_changed", () => {
        const newCountry = getCurrentCountry();
        const newLanguage = getCurrentLocale();
        // Re-toggle active class sur tous les clones
        this.querySelectorAll<HTMLElement>('[data-lmdr-market-clone="true"]').forEach((c) => {
          c.classList.toggle(
            "lmdr-market-active",
            c.getAttribute("data-country-code") === newCountry,
          );
        });
        // Re-hydrate current display (depuis fetchMarkets cache)
        void fetchMarkets().then((s) => {
          const m = findCurrentMarket(s.countries, newCountry);
          hydrateCurrent(this, m, newLanguage);
        });
      });
    }
  }
}

export function registerMarketsSwitcher(): void {
  if (typeof customElements === "undefined") return;
  if (customElements.get("smootify-markets-switcher")) return;
  customElements.define("smootify-markets-switcher", SmootifyMarketsSwitcherElement);
}
